import React, { useState, useEffect, useContext } from "react";
import {
  Package,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Eye,
  Mail,
  Star,
  FileText,
  AlertCircle,
  Loader,
  Globe,
  Calendar,
  ArrowUpRight,
  Edit,
  Trash2,
} from "lucide-react";

import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../Context/AuthContextProvider";
import { get, del } from "../../../utils/api";

const MySubmissions = () => {
  const { loginUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState("all"); // all, pending, approved, rejected
  const [deleteLoading, setDeleteLoading] = useState(null);

  useEffect(() => {
    if (loginUser) {
      fetchSubmissions();
    }
  }, [loginUser, filter]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      // Security: Always require userId - no fallback to username
      if (!loginUser?._id) {
        toast.error("User authentication required");
        setLoading(false);
        return;
      }

      const response = await get(`/domain/mysubmissions/${loginUser._id}`);

      if (response.success) {
        setStats(response.stats);

        // Filter submissions based on selected filter
        let filteredSubmissions = response.submissions;
        if (filter === "pending") {
          filteredSubmissions = response.submissions.filter(
            (s) => s.status === "pending"
          );
        } else if (filter === "approved") {
          filteredSubmissions = response.submissions.filter(
            (s) => s.status === "Active"
          );
        } else if (filter === "rejected") {
          filteredSubmissions = response.submissions.filter(
            (s) => s.status === "removed"
          );
        }

        setSubmissions(filteredSubmissions);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("User not found");
      } else if (error.response?.status === 400) {
        toast.error("Invalid user ID");
      } else {
        toast.error(error.response?.data?.error || "Failed to load your submissions");
      }
    } finally{
      setLoading(false);
    }
  };

  const handleEdit = (submissionId) => {
    navigate(`/Dashboard/EditSubmission/${submissionId}`);
  };

  const handleDelete = async (submissionId, domainName) => {
    if (!window.confirm(`Are you sure you want to delete ${domainName}?`)) {
      return;
    }

    setDeleteLoading(submissionId);
    try {
      const response = await del(`/domain/deletesubmission/${submissionId}`, {
        data: { userId: loginUser._id || loginUser.id },
      });

      if (response.success) {
        toast.success("Domain submission deleted successfully");
        fetchSubmissions();
      }
    } catch (error) {
      toast.error("Failed to delete submission");
    } finally {
      setDeleteLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        border: "border-yellow-300",
        icon: <Clock className="w-4 h-4" />,
        label: "Pending Review",
      },
      Active: {
        bg: "bg-green-100",
        text: "text-green-800",
        border: "border-green-300",
        icon: <CheckCircle className="w-4 h-4" />,
        label: "Approved",
      },
      removed: {
        bg: "bg-red-100",
        text: "text-red-800",
        border: "border-red-300",
        icon: <XCircle className="w-4 h-4" />,
        label: "Rejected",
      },
      sold: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        border: "border-blue-300",
        icon: <TrendingUp className="w-4 h-4" />,
        label: "Sold",
      },
    };

    const badge = badges[status] || badges.pending;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border-2 ${badge.bg} ${badge.text} ${badge.border}`}
      >
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
    <div
      className={`${bgColor} rounded-xl p-6 border-2 border-${color}-200 shadow-md hover:shadow-lg transition-all`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium mb-1">{label}</p>
          <p className={`text-3xl font-bold text-${color}-600`}>{value}</p>
        </div>
        <div className={`bg-${color}-100 p-3 rounded-xl`}>
          <Icon className={`w-8 h-8 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-24 flex items-center justify-center">
          <div className="text-center">
            <Loader className="w-12 h-12 text-[#d4af37] animate-spin mx-auto mb-4" />
            <p className="text-gray-600 font-medium">
              Loading your submissions...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-24">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-2 border-[#d4af37]">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-[#b99653] to-[#d4af37]">
                  <Package className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-[#b99653] to-[#d4af37] bg-clip-text text-transparent">
                    My Submissions
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Track all your domain submissions and their status
                  </p>
                </div>
              </div>
              <Link
                to="/post-dot-gold-domain"
                className="px-6 py-3 bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Globe className="w-5 h-5" />
                Submit New Domain
              </Link>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                icon={Package}
                label="Total Submissions"
                value={stats.total}
                color="blue"
                bgColor="bg-blue-50"
              />
              <StatCard
                icon={Clock}
                label="Pending Review"
                value={stats.pending}
                color="yellow"
                bgColor="bg-yellow-50"
              />
              <StatCard
                icon={CheckCircle}
                label="Approved"
                value={stats.approved}
                color="green"
                bgColor="bg-green-50"
              />
              <StatCard
                icon={XCircle}
                label="Rejected"
                value={stats.rejected}
                color="red"
                bgColor="bg-red-50"
              />
            </div>
          )}

          {/* Filter Tabs */}
          <div className="bg-white rounded-xl shadow-md p-4 mb-6 border-2 border-gray-200">
            <div className="flex gap-2 flex-wrap">
              {[
                {
                  key: "all",
                  label: "All Submissions",
                  count: stats?.total || 0,
                },
                {
                  key: "pending",
                  label: "Pending",
                  count: stats?.pending || 0,
                },
                {
                  key: "approved",
                  label: "Approved",
                  count: stats?.approved || 0,
                },
                {
                  key: "rejected",
                  label: "Rejected",
                  count: stats?.rejected || 0,
                },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`px-6 py-2.5 rounded-lg font-semibold transition-all ${
                    filter === tab.key
                      ? "bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>
          </div>

          {/* Submissions List */}
          {submissions.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center border-2 border-gray-200">
              <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                No Submissions Found
              </h3>
              <p className="text-gray-600 mb-6">
                {filter === "all"
                  ? "You haven't submitted any domains yet."
                  : `You don't have any ${filter} submissions.`}
              </p>
              <Link
                to="/post-dot-gold-domain"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                <Globe className="w-5 h-5" />
                Submit Your First Domain
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => (
                <div
                  key={submission._id}
                  className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200 hover:border-[#d4af37] transition-all"
                >
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    {/* Left Section */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <h3 className="text-2xl font-bold text-[#101c4c] break-all">
                          {submission.name}
                        </h3>
                        {submission.premium && (
                          <span className="bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                            <Star className="w-3 h-3 fill-current" />
                            Premium
                          </span>
                        )}
                        {getStatusBadge(submission.status)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center gap-2 text-gray-700">
                          <span className="font-semibold">
                            ₹{submission.price?.toLocaleString("en-IN")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <Calendar className="w-5 h-5 text-blue-500" />
                          <span>
                            {new Date(submission.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <FileText className="w-5 h-5 text-purple-500" />
                          <span>{submission.category}</span>
                        </div>
                      </div>

                      {submission.description && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {submission.description}
                        </p>
                      )}

                      {/* Stats Row with Clickable Inquiries */}
                      {submission.status === "Active" ? (
                        <div className="flex items-center gap-6 text-sm">
                          <div className="flex items-center gap-1.5">
                            <Eye className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-700 font-medium">
                              {submission.views || 0} views
                            </span>
                          </div>
                          <Link
                            to={`/Dashboard/Inquiries/${submission._id}`}
                            className="flex items-center gap-1.5 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-all border border-transparent hover:border-blue-200 group"
                          >
                            <Mail className="w-4 h-4 text-gray-500 group-hover:text-blue-600 transition-colors" />
                            <span className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors">
                              {submission.inquiries?.length || 0} inquiries
                            </span>
                            <ArrowUpRight className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 group-hover:text-blue-600 transition-all" />
                          </Link>
                        </div>
                      ) : submission.status === "pending" ? (
                        <div className="flex items-center gap-2 text-sm text-yellow-600 bg-yellow-50 px-4 py-2 rounded-lg border border-yellow-200">
                          <AlertCircle className="w-4 h-4" />
                          <span>
                            Analytics will be available once your submission is approved
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
                          <AlertCircle className="w-4 h-4" />
                          <span>
                            No analytics available for this submission
                          </span>
                        </div>
                      )}

                      {/* Action Buttons - Only for pending submissions */}
                      {submission.status === "pending" && (
                        <div className="flex gap-3 mt-4">
                          <button
                            onClick={() => handleEdit(submission._id)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-all"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDelete(submission._id, submission.name)
                            }
                            disabled={deleteLoading === submission._id}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {deleteLoading === submission._id ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                            Delete
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Right Section - Images */}
                    {submission.media && submission.media.length > 0 && (
                      <div className="flex gap-2">
                        {submission.media.slice(0, 3).map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`${submission.name} ${idx + 1}`}
                            className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
                          />
                        ))}
                        {submission.media.length > 3 && (
                          <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-gray-200 flex items-center justify-center">
                            <span className="text-gray-600 font-semibold">
                              +{submission.media.length - 3}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Status Message */}
                  {submission.status === "pending" && (
                    <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                        <p className="text-sm text-yellow-800">
                          Your submission is under admin review. You can edit or
                          delete it while it's pending. You'll be notified via
                          email once reviewed.
                        </p>
                      </div>
                    </div>
                  )}

                  {submission.status === "Active" && (
                    <div className="mt-4 p-3 bg-green-50 border-l-4 border-green-400 rounded">
                      <div className="flex items-center gap-2 justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <p className="text-sm text-green-800">
                            Your domain is now live on the marketplace!
                          </p>
                        </div>
                        <Link
                          to={`/Marketplace_Product/${submission._id}`}
                          className="inline-flex items-center gap-1 text-green-700 hover:text-green-900 font-semibold text-sm"
                        >
                          View Listing
                          <ArrowUpRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  )}

                  {submission.status === "removed" && (
                    <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-400 rounded">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-600" />
                        <p className="text-sm text-red-800">
                          This submission was rejected. Please contact support
                          for more information.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MySubmissions;